// IMPORTANT: BaseHelpPopup is only for internal use.
// Consumer should only be using HelpPopup
import { forwardRef, Fragment, Suspense, useRef, useState } from 'react';
import { HelpButton, HelpButtonToolbar } from '@sas/react-svg';
import { HelpPopupContent } from '.';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { Button, ButtonVariant } from '../../Button';
import { getMenuButtonTokens } from '../../MenuButton/__internal__/BaseMenuButton';
import { Popover } from '../../Popover/Popover';
import { useHelpPopupA11yInfo } from '../useHelpPopupA11yInfo';
import cn from './BaseHelpPopup.css.json';
import './BaseHelpPopup.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BaseHelpPopupComponent = /*#__PURE__*/forwardRef((props, ref) => {
  // Help Popup normally controls whether its popover is open using its button, but can be controlled externally with isOpen and setIsOpen
  const [popupOpenInternal, setPopupOpenInternal] = useState(false);
  // If setIsOpen is defined and isOpen has been passed in, use it to control the open status, otherwise use the value that is tracked internally
  const popupOpen = props.isOpen !== undefined && props.setIsOpen !== undefined ? props.isOpen : popupOpenInternal;
  const icon = props.variant === 'toolbar' ? HelpButtonToolbar : HelpButton;
  const buttonRef = useRef(null);
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars({
    pd: dt.tokens['space-base-m'],
    min: dt.tokens['size-container-xxs'],
    'font-size-l': dt.tokens['font-size-body-text-l']
  }, 'help-popup-container');
  const {
    buttonTooltip,
    buttonAriaLabel,
    dialogAriaLabel
  } = useHelpPopupA11yInfo(props);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Button, {
      style: {
        display: 'inline-flex'
      },
      disabled: props.disabled,
      testId: postfixTestId(props.testId, 'button'),
      ref: element => {
        buttonRef.current = element;
        setRef(props.buttonRef, element);
      },
      onPress: () => {
        setPopupOpenInternal(true);
        props.setIsOpen?.(true);
      },
      icon: {
        icon
      },
      tooltip: buttonTooltip,
      ariaLabel: buttonAriaLabel,
      variant: ButtonVariant.Icon,
      className: props.className,
      focusable: props.focusable ?? true,
      tokens: () => getMenuButtonTokens(popupOpenInternal, ButtonVariant.Icon, undefined, dt)
    }), /*#__PURE__*/_jsx(Popover, {
      testId: props.testId,
      referenceElementRef: buttonRef,
      isOpen: popupOpen,
      onClose: () => {
        setPopupOpenInternal(false);
        props.setIsOpen?.(false);
        props.onClose?.();
      },
      onEscape: props.onEscape,
      ariaLabel: dialogAriaLabel,
      role: "dialog",
      style: {
        ...tokens,
        maxWidth: `${dt.tokens['size-container-l']}`,
        maxHeight: '50vh'
      },
      focusable: true,
      className: cn.container,
      placement: "auto",
      ref: ref,
      children: /*#__PURE__*/_jsx(HelpPopupContent, {
        ...props
      })
    })]
  });
});

// Suspense is necessary to prevent errors from promises thrown by useTextLocalizer when the library bundle isn't loaded yet
// useTextLocalizer is called in useHelpPopupA11yInfo so we have to wrap this whole component in Suspense
export const BaseHelpPopup = /*#__PURE__*/forwardRef((props, ref) => {
  return /*#__PURE__*/_jsx(Suspense, {
    fallback: null,
    children: /*#__PURE__*/_jsx(BaseHelpPopupComponent, {
      ...props,
      ref: ref
    })
  });
});
//# sourceMappingURL=BaseHelpPopup.js.map